import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Video, CalendarDays, StickyNote, Users, User } from 'lucide-react';
import { nurseAPI } from '../../services/api';
import { tokenManager } from '../../services/tokenManager';
import { toast } from 'react-toastify';
import { getImageUrl } from '../../utils/imageUtils';
import PatientDetailsPopup from '../../components/PatientDetailsPopup';
import SessionNotesDialog from '../../components/SessionNotesDialog';
import { FaQuestion } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useNavigate } from 'react-router-dom';




const NurseDashboard = () => {
  
  const navigate = useNavigate();
  const redirectToCourse = () => {
    setTimeout(() => {
      navigate('/nurse/adhdcourse');
    }, 500);
  };


  const baseURL =  process.env.REACT_APP_BASE_URL || 'https://backend.adhdrapidcare.com';
  
  const user = JSON.parse(localStorage.getItem("user")) || { name: "User" };
  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric"
  });

  const [loading, setLoading] = useState(true);
  const [todaySessions, setTodaySessions] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    todaySessions: 0,
    pendingSessions: 0,
    completedThisMonth: 0,
    assignedPatientsCount: 0
  });
  const [assignedPatients, setAssignedPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showNotesPopup, setShowNotesPopup] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showingNotesFor, setShowingNotesFor] = useState(null); // Track which session is showing notes
  const [sessionNotes, setSessionNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showingAddNotesFor, setShowingAddNotesFor] = useState(null); // Track which session should show Add Notes button

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const user = tokenManager.getUser();
      if (!user || !user.id) {
        toast.error('User information not found');
        return;
      }

      const today = new Date().toISOString().split('T')[0];

      // Initial fetch of all data
      const [statsResponse, sessionsResponse, patientsResponse] = await Promise.all([
        nurseAPI.getNurseDashboardStats(user.id),
        nurseAPI.getNurseSchedule(user.id, { date: today }),
        nurseAPI.getNurseAssignedPatients(user.id)
      ]);

      setDashboardStats(statsResponse);
      setAssignedPatients(patientsResponse.patients);

      const validSessions = sessionsResponse.sessions
        .filter(session => session.status !== 'completed')
        .map(session => ({
          _id: session._id,
          startTime: session.startTime || 'N/A',
          endTime: session.endTime || 'N/A',
          patientName: session.patientName || session.patientId?.name || 'Unknown Patient',
          sessionType: session.sessionType || 'Regular Session',
          status: session.status || 'scheduled',
          sessionMode: session.sessionMode || 'video',
          aiReviewed: session.aiReviewed || false,
          zoomStartUrl: session.zoomStartUrl,
          zoomJoinUrl: session.zoomJoinUrl,
          monthlyAssessmentCompleted: session.monthlyAssessmentCompleted || false,
          totalSessions: 2, // Set to 2 to show monthly assessment requirement
          sendMail: session.sendMail || false,
          date: session.date
        }));

      setTodaySessions(validSessions);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Filter patients based on search query
    if (searchQuery.trim()) {
      const filtered = assignedPatients.filter(patient => 
        patient.user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        patient.diagnosis?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredPatients(filtered);
    } else {
      setFilteredPatients(assignedPatients);
    }
  }, [searchQuery, assignedPatients]);

  const stats = {
    todaySessions: 6,
    pendingSessions: 3,
    assignedPatientsCount: 28
  };

  const patients = [
    {
      name: "Sarah Wilson",
      diagnosis: "ADHD / Under Review",
      status: "Active",
      nextVisit: "22 June 2025 at 10:00 AM",
      image: "/images/Akriti Mehta.png"
    },
    {
      name: "Michael Brown",
      diagnosis: "ADHD / Confirmed",
      status: "Paused",
      nextVisit: "25 June 2025 at 2:00 PM",
      image: "/images/Akriti Mehta.png"
    }
  ];

  const handleStartSession = async (session) => {
    try {
      // Update session status to in_progress
      await nurseAPI.updateNurseSessionStatus(session._id, 'in_progress');

      // Open Zoom meeting in new tab if it's a video session
      if (session.sessionMode === 'video' && session.zoomStartUrl) {
        window.open(session.zoomStartUrl, '_blank', 'noopener,noreferrer');
      }

      // Refresh the dashboard data
      fetchDashboardData();
      toast.success('Session started successfully');
    } catch (error) {
      console.error('Error starting session:', error);
      toast.error('Failed to start session. Please try again.');
    }
  };

  const handleCompleteSession = async (session) => {
    // Don't mark as completed yet, just show the Add Notes button
    setShowingAddNotesFor(session._id);
  };

  const handleSaveNotes = async () => {
    if (!selectedSession) return;

    try {
      setSavingNotes(true);

      // First mark the session as completed
      await nurseAPI.updateNurseSessionStatus(selectedSession._id, 'completed');

      // Then save the notes
      await nurseAPI.addSessionNotes(selectedSession._id, {
        notes: sessionNotes.trim()
      });

      setShowNotesPopup(false);
      setSelectedSession(null);
      setSessionNotes('');
      setShowingAddNotesFor(null);
      fetchDashboardData();
      toast.success('Session completed and notes saved successfully');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to complete session. Please try again.');
    } finally {
      setSavingNotes(false);
    }
  };

  const formatSessionType = (type) => {
    if (!type) return 'Regular Session';
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const checkSessionActive = (session) => {
    const now = new Date();
    const sessionDate = new Date(session.date);
    const [time, period] = session.startTime.split(' ');
    const [hours, minutes] = time.split(':');
    let sessionHours = parseInt(hours);

    if (period === 'PM' && sessionHours !== 12) {
      sessionHours += 12;
    } else if (period === 'AM' && sessionHours === 12) {
      sessionHours = 0;
    }

    sessionDate.setHours(sessionHours, parseInt(minutes), 0, 0);

    // Get current time and session time in minutes since midnight
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const sessionMinutes = sessionHours * 60 + parseInt(minutes);
    
    // Calculate difference in minutes
    const minutesDiff = sessionMinutes - currentMinutes;

    // Active if within 10 minutes before start time
    return minutesDiff <= 10 && minutesDiff >= 0;
  };

  // Add this function to handle opening the popup
  const handleViewProfile = (patient, e) => {
    e.preventDefault();
    setSelectedPatient(patient);
  };

  // Modify the renderAssignedPatients function
  const renderAssignedPatients = () => (
    <div className="bg-white shadow rounded-lg p-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
        <h2 className="text-lg font-semibold text-gray-800">Assigned Patients</h2>
        <div className="flex items-center gap-4 w-full md:w-auto">
          <input
            type="text"
            placeholder="Search patients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full md:w-64 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Link 
            to="/nurse/patients"
            className="text-blue-600 hover:text-blue-800 text-sm font-medium whitespace-nowrap"
          >
            View All
          </Link>
        </div>
      </div>

      {/* Table Header */}
      <div className="hidden md:grid grid-cols-4 gap-4 px-4 py-2 bg-gray-50 rounded-t-lg text-sm font-medium text-gray-600">
        <div>Patient Name</div>
        <div>Diagnosis Status</div>
        <div>Subscription</div>
        <div>Actions</div>
      </div>

      {/* Table Content */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : filteredPatients.length > 0 ? (
          filteredPatients.slice(0, 5).map((patient) => (
              patient &&
              (
                   <div key={patient._id} className="grid md:grid-cols-4 gap-4 p-4 border rounded-lg items-center">
              {/* Patient Name & Image */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                  {patient?.user?.profileImage ? (
                    <img
                      src={getImageUrl(patient.user.profileImage)}
                      alt={patient.user.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-6 h-6 text-gray-500" />
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{patient.user?.name}</h3>
                </div>
              </div>

              {/* Diagnosis Status */}
              <div className="text-sm text-gray-600">
                {patient.care_plan_onboarding_completed ? 'Completed' : 'Not Completed'}
              </div>

              {/* Subscription Status */}
              <div className="flex items-center gap-2">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${patient.subscription_status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                    }`}
                >
                  {patient.subscription_status || 'Active'}
                </span>
                <FaQuestion
                  size={14}
                  className="text-gray-600 border border-gray-600 rounded-full p-0.5 cursor-pointer"
                  data-tooltip-id={`subscription-tooltip-${patient._id}`}
                  data-tooltip-content={patient.subscription_status === 'active' ? 'Ready for sessions' : 'Subscription temporarily suspended'}
                />
                <Tooltip id={`subscription-tooltip-${patient._id}`} place="top" effect="solid" />
              </div>


             

              {/* Actions */}
              <div>
                <button
                  onClick={(e) => handleViewProfile(patient, e)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View Profile
                </button>
              </div>
            </div>
              )
          ))
        ) : (
          <div className="text-center py-4 text-gray-500">
            No patients found
          </div>
        )}
      </div>

      {/* Add the PatientDetailsPopup */}
      {selectedPatient && (
        <PatientDetailsPopup
          patient={selectedPatient}
          onClose={() => setSelectedPatient(null)}
        />
      )}
    </div>
  );

  return (
    <div className=" ">
      {/* Top Stats */}
      <div className="mb-6 md:mb-8">
        <h2 className="text-xl md:text-2xl font-semibold text-gray-800">
          Welcome back, Nurse {user?.name?.split(' ')[0]?.charAt(0)?.toUpperCase() + user?.name?.split(' ')[0]?.slice(1) || 'User'}
        </h2>
        <p className="text-gray-600 mt-2">{formattedDate}</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl p-4 border border-gray-200 shadow-md bg-green-50">
          <div className="w-fit mb-3">
            <span className="bg-green-100 text-green-600 p-2 rounded-lg inline-flex items-center justify-center">
              <CalendarDays className="w-5 h-5" />
            </span>
          </div>

          {/* Number + ❓ */}
          <div className="flex items-center gap-1 relative">
            <p className="text-2xl font-bold text-gray-900">{dashboardStats.todaySessions}</p>
            <FaQuestion
              size={14}
              className="text-gray-600 border border-gray-600 rounded-full p-0.5 cursor-pointer"
              data-tooltip-id="sessions-tooltip"
              data-tooltip-content="Sessions completed today"
            />
            <Tooltip 
              id="sessions-tooltip" 
              place="top" 
              effect="solid" 
              className="z-[9999]"
              style={{ zIndex: 9999 }}
            />
          </div>

          <div className="flex items-center justify-between mt-1">
            <span className="text-sm text-gray-500">Sessions Today</span>
          </div>
        </div>


        {/* <div className="rounded-2xl p-4 border border-gray-200 shadow-md bg-yellow-50">
          <div className="w-fit mb-3">
            <span className="bg-yellow-100 text-yellow-600 p-2 rounded-lg inline-flex items-center justify-center">
              <StickyNote className="w-5 h-5" />
            </span>
          </div>
          <span className="text-2xl font-bold text-gray-900">{dashboardStats.pendingSessions}</span>
          <div className="flex items-center justify-between mt-1">
            <div className="text-sm text-gray-500">Notes Pending</div>
            <span className="text-sm text-yellow-600 font-medium">Action required</span>
          </div>
        </div> */}

        <div className="rounded-2xl p-4 border border-gray-200 shadow-md bg-purple-50">
          <div className="w-fit mb-3">
            <span className="bg-purple-100 text-purple-600 p-2 rounded-lg inline-flex items-center justify-center">
              <Users className="w-5 h-5" />
            </span>
          </div>

          {/* Number + ❓ icon */}
          <div className="flex items-center gap-1">
            <span className="text-2xl font-bold text-gray-900">{dashboardStats.assignedPatientsCount}</span>
            <FaQuestion
              size={14}
              className="text-gray-600 border border-gray-600 rounded-full p-0.5 cursor-pointer"
              data-tooltip-id="assigned-patients-tooltip"
              data-tooltip-content="Total patients currently assigned to you"
            />
            <Tooltip id="assigned-patients-tooltip" place="top" effect="solid" />
          </div>

          <div className="flex items-center justify-between mt-1">
            <div className="text-sm text-gray-500">Active Patients</div>
          </div>
        </div>

      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Today's Sessions */}
        <div className="md:col-span-3 space-y-6">
          <div className="bg-white shadow rounded-lg p-5">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Today's Sessions</h2>
              {todaySessions.length > 3 && (
                <Link 
                  to="/nurse/sessions"
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View All
                </Link>
              )}
            </div>

            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : todaySessions.length > 0 ? (
                todaySessions.slice(0, 3).map((session) => (
                  <div key={session._id} className="flex justify-between items-center p-4 border border-gray-200 rounded-lg bg-white hover:border-blue-400 hover:bg-blue-50 transition mb-3">
                    <div>
                      <div className="flex items-center gap-2 text-gray-600 font-medium">
                        <span>{session.startTime} - {session.endTime}</span>
                        {session.sessionMode === 'video' && <Video className="w-4 h-4" />}
                      </div>
                      
                      <div className="text-sm text-gray-500">{session.patientName}</div>
                      <div className="text-sm text-gray-500">{formatSessionType(session.sessionType)}</div>
                      {session.sessionType === 'monthly_checkin' && !session.monthlyAssessmentCompleted && session.status === 'scheduled' && (
                        <div className="text-sm text-red-600">
                          * Monthly assessment not completed
                        </div>
                      )}
                      {!checkSessionActive(session) && session.status === 'scheduled' && (
                        <div className="text-sm text-gray-600">
                          * Session will be active 10 minutes before start time
                        </div>
                      )}

                    </div>

                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        session.status === 'completed' ? 'bg-green-100 text-green-800' :
                        session.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        session.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        session.status === 'missed' ? 'bg-gray-100 text-gray-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {session.status.split('_').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                        ).join(' ')}
                      </span>

                      {/* Show Start Video Session button for scheduled sessions */}
                      {session.status === 'scheduled' && session.sessionMode === 'video' && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              const isActive = checkSessionActive(session);
                              if (!isActive) return;
                              
                              // For first session, only check time
                              // For other sessions, check monthly assessment if it's a monthly check-in
                              const canStart = session.totalSessions === 1 || 
                                             session.sessionType !== 'monthly_checkin' ||
                                             session.monthlyAssessmentCompleted;
                              
                              if (canStart) {
                                handleStartSession(session);
                              }
                            }}
                            disabled={
                              !checkSessionActive(session) ||
                              (session.totalSessions > 1 &&
                                session.sessionType === 'monthly_checkin' &&
                                !session.monthlyAssessmentCompleted)
                            }
                            className={`flex items-center gap-1 ${!checkSessionActive(session) ||
                                (session.totalSessions > 1 &&
                                  session.sessionType === 'monthly_checkin' &&
                                  !session.monthlyAssessmentCompleted)
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-500 hover:bg-blue-600'
                              } text-white text-sm px-3 py-1.5 rounded-md font-medium`}
                          >
                            <Video className="w-4 h-4 text-white" />
                            Start Video Session
                          </button>

                        
                          <FaQuestion
                            size={14}
                            className="text-gray-600 border border-gray-600 rounded-full p-0.5 cursor-pointer"
                            data-tooltip-id={`start-session-tooltip-${session._id}`}
                            data-tooltip-content="Click 'Start Session' when beginning, 'Complete Session' when finished. This tracks time for payroll."
                          />
                          <Tooltip id={`start-session-tooltip-${session._id}`} place="top" effect="solid" />
                        </div>
                      )}


                      {/* Show Complete Session button for in-progress sessions */}
                      {session.status === 'in_progress' && session.sessionMode === 'video' && showingAddNotesFor !== session._id && (
                        <button
                          onClick={() => handleCompleteSession(session)}
                          className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white text-sm px-3 py-1.5 rounded-md font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Complete Session
                        </button>
                      )}

                      {/* Show Add Session Notes button after clicking Complete */}
                      {showingAddNotesFor === session._id && (
                        <button
                          onClick={() => {
                            setSelectedSession(session);
                            setShowNotesPopup(true);
                          }}
                          className="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white text-sm px-3 py-1.5 rounded-md font-medium"
                        >
                          <StickyNote className="w-4 h-4" />
                          Add Session Notes
                        </button>
                      )}

                      {/* Show Notes Added indicator for completed sessions with notes */}
                      {session.status === 'completed' && session.sessionNotes?.content && (
                        <div className="flex items-center gap-1 text-green-600">
                          <StickyNote className="w-4 h-4" />
                          <span className="text-xs">Notes Added</span>
                        </div>
                      )}

                      {/* Show AI Results button if available */}
                      {session.aiReviewed && (
                        <>
                          <button
                            onClick={() => window.open(`/monthly-assessment-result/${session._id}`, '_blank')}
                            className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-md hover:bg-purple-200 transition-colors"
                          >
                            AI Results
                          </button>
                          <FaQuestion
                            size={14}
                            className="ml-2 text-gray-600 border border-gray-600 rounded-full p-0.5 cursor-pointer"
                            data-tooltip-id={`ai-analysis-tooltip-${session._id}`}
                            data-tooltip-content="Click to view patient insights before starting session. Only appears when monthly assessment is completed."
                          />
                          <Tooltip id={`ai-analysis-tooltip-${session._id}`} place="top" effect="solid" />
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-4">
                  No sessions scheduled for today
                </div>
              )}
            </div>
          </div>

          {/* Assigned Patients */}
          {renderAssignedPatients()}
        </div>

        {/* Alerts & Quick Actions */}
        <div className="space-y-6">
          {/* Alerts */}
          <div className=" hidden bg-white shadow rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Alerts & Reminders</h2>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3 bg-red-50 text-red-700 px-4 py-3 rounded-lg">
                <svg className="w-5 h-5 mt-0.5 text-red-600 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 4a8 8 0 100 16 8 8 0 000-16z" />
                </svg>
                <span>3 session notes pending completion</span>
              </li>
              <li className="flex items-start gap-3 bg-yellow-50 text-yellow-800 px-4 py-3 rounded-lg">
                <img src="/images/MARGIN_WRAPPER.svg" alt="Reminder Icon" className="w-5 h-5 object-contain" />
                <span>2 patients due for review</span>
              </li>
              <li className="flex items-start gap-3 bg-blue-50 text-blue-800 px-4 py-3 rounded-lg">
                <svg className="w-5 h-5 mt-0.5 text-blue-600 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M12 4a8 8 0 100 16 8 8 0 000-16z" />
                </svg>
                <span>New protocol update available</span>
              </li>
            </ul>
          </div>   
        </div>

      </div>

      {/* Notes Popup */}
      {showNotesPopup && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Add Session Notes</h2>
              <button
                onClick={() => {
                  setShowNotesPopup(false);
                  setSelectedSession(null);
                  setSessionNotes('');
                  setShowingAddNotesFor(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <textarea
              value={sessionNotes}
              onChange={(e) => setSessionNotes(e.target.value)}
              placeholder="Enter your session notes here..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="6"
            />

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowNotesPopup(false);
                  setSelectedSession(null);
                  setSessionNotes('');
                  setShowingAddNotesFor(null);
                }}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNotes}
                disabled={savingNotes || !sessionNotes.trim()}
                className={`px-6 py-2 rounded-lg font-medium ${
                  savingNotes || !sessionNotes.trim()
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {savingNotes ? 'Saving...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NurseDashboard;